import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { Home, Search, Bell, Bookmark, Images, Settings } from 'lucide-react'
import { useState, useEffect } from 'react'
import notificationService from '../services/notifications'
import CreatePostModal from './CreatePostModal'
import { useAuth } from '../contexts/AuthContext'

export default function Sidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    // Initialize notification service
    notificationService.connect()

    // Get initial unread count
    setUnreadCount(notificationService.getUnreadCount())

    // Subscribe to new notifications
    const unsubscribe = notificationService.onNotification(() => {
      setUnreadCount(notificationService.getUnreadCount())
    })

    // Cleanup on unmount
    return () => {
      unsubscribe()
    }
  }, [])

  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Search, label: 'Explore', path: '/explore' },
    { icon: Bell, label: 'Notifications', path: '/notifications', badge: unreadCount > 0 ? unreadCount : undefined },
    { icon: Bookmark, label: 'Bookmarks', path: '/bookmarks' },
    { icon: Images, label: 'Gallery', path: '/gallery' },
  ]

  return (
    <>
      <div className="w-20 xl:w-64 flex flex-col h-screen sticky top-0 px-2 xl:px-4 py-2 items-center xl:items-stretch transition-all duration-200 overflow-hidden">
        {/* OnRecord Logo */}
        <div className="mb-2 p-3 flex items-center gap-2">
          <img src="/logo.svg" alt="on record" className="w-9 h-9" />
          <img src="/onrecord-text-logo.png" alt="ON RECORD" className="h-5 hidden xl:block" style={{ filter: 'brightness(0)' }} />
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={(e) => {
                if (item.path === '/' && location.pathname === '/') {
                  e.preventDefault()
                  window.dispatchEvent(new CustomEvent('home-tap'))
                }
              }}
              style={({ isActive }) => ({
                color: isActive ? 'var(--solar)' : 'var(--smoke)',
                fontWeight: isActive ? '700' : '500'
              })}
              className="nav-item flex items-center gap-4 px-4 py-3 rounded-full text-xl transition-colors relative"
            >
              <item.icon className="w-6 h-6" strokeWidth={2} />
              <span className="hidden xl:inline">{item.label}</span>
              {item.badge && (
                <span
                  className="absolute left-8 top-2 text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold"
                  style={{
                    background: 'var(--solar)',
                    color: 'var(--saturn)'
                  }}
                >
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}

          {/* Settings with gear icon */}
          <NavLink
            to="/settings"
            style={({ isActive }) => ({
              color: isActive ? 'var(--solar)' : 'var(--smoke)',
              fontWeight: isActive ? '700' : '500'
            })}
            className="nav-item flex items-center gap-4 px-4 py-3 rounded-full text-xl transition-colors"
          >
            <Settings className="w-6 h-6" strokeWidth={2} />
            <span className="hidden xl:inline">Settings</span>
          </NavLink>
        </nav>

        {/* User Profile Section at Bottom - Twitter Style */}
        <div
          className="mb-3 p-3 flex items-center gap-3 rounded-full cursor-pointer relative transition-colors"
          onClick={() => setShowProfileMenu(!showProfileMenu)}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.03)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          {/* User Avatar */}
          {user?.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-onrecord-orange flex items-center justify-center text-white font-bold">
              {(user?.name && user.name !== 'User' && user.name !== 'Your Name')
                ? user.name.charAt(0).toUpperCase()
                : (user?.username?.charAt(0).toUpperCase() || 'U')}
            </div>
          )}

          {/* User Info */}
          <div className="flex-1 min-w-0 hidden xl:block">
            <div className="font-bold text-sm text-gray-900 truncate">
              {(user?.name && user.name !== 'User' && user.name !== 'Your Name' && user.name !== '')
                ? user.name
                : user?.username || 'User'}
            </div>
            <div className="text-sm text-gray-500 truncate">
              @{user?.username || 'username'}
            </div>
          </div>

          {/* Three Dots Menu Icon */}
          <div className="text-gray-500 hidden xl:block">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="1.5"/>
              <circle cx="12" cy="12" r="1.5"/>
              <circle cx="12" cy="19" r="1.5"/>
            </svg>
          </div>

          {/* Profile Dropdown Menu */}
          {showProfileMenu && (
            <div className="absolute bottom-full left-0 mb-2 w-full rounded-2xl shadow-2xl py-2 z-50 bg-white border border-gray-200">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowProfileMenu(false)
                  navigate('/profile')
                }}
                className="block w-full text-left px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 transition-colors font-medium"
              >
                View Profile
              </button>
              <div className="border-t border-gray-200 my-1"></div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowProfileMenu(false)
                  logout()
                }}
                className="block w-full text-left px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 transition-colors font-medium"
              >
                Log out
              </button>
            </div>
          )}
        </div>

        {/* Create Post Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="w-12 xl:w-full font-bold text-[15px] py-3 rounded-full transition-all mb-3 text-white mx-auto xl:mx-0"
          style={{
            background: 'rgb(29, 155, 240)',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(26, 140, 216)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgb(29, 155, 240)'}
        >
          <span className="hidden xl:inline">Post</span>
          <span className="xl:hidden text-xl">+</span>
        </button>
      </div>

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </>
  )
}
